import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { normalizeRect, type RedactionRect } from '../platform/redaction';

export function SourcePreview({ file, redactions, onChange }:{ file:File; redactions:RedactionRect[]; onChange:(next:RedactionRect[])=>void }) {
  const source = useMemo(() => URL.createObjectURL(file), [file]);
  const [selected, setSelected] = useState<number|null>(redactions.length ? redactions.length - 1 : null);
  const start = useRef<{x:number;y:number}|null>(null);
  const frame = useRef<HTMLDivElement>(null);
  const revokeTimer = useRef<number|undefined>(undefined);

  useEffect(() => {
    if (revokeTimer.current) window.clearTimeout(revokeTimer.current);
    return () => { revokeTimer.current = window.setTimeout(() => URL.revokeObjectURL(source), 0); };
  }, [source]);

  const point = (event:PointerEvent) => {
    const bounds = frame.current!.getBoundingClientRect();
    return { x:(event.clientX - bounds.left) / bounds.width * 100, y:(event.clientY - bounds.top) / bounds.height * 100 };
  };
  const begin = (event:PointerEvent) => { event.currentTarget.setPointerCapture(event.pointerId); start.current = point(event); };
  const finish = (event:PointerEvent) => {
    if (!start.current) return;
    const end = point(event); const origin = start.current; start.current = null;
    const rect = normalizeRect({ x:Math.min(origin.x,end.x), y:Math.min(origin.y,end.y), width:Math.abs(end.x-origin.x), height:Math.abs(end.y-origin.y) });
    const next = [...redactions, rect]; onChange(next); setSelected(next.length - 1);
  };
  const add = () => { const next=[...redactions,{x:10,y:10,width:45,height:12}];onChange(next);setSelected(next.length-1); };
  const update = (key:keyof RedactionRect, value:number) => {
    if (selected === null) return;
    const next = redactions.map((rect,index) => index === selected ? normalizeRect({...rect,[key]:value}) : rect);
    onChange(next);
  };
  const active = selected === null ? null : redactions[selected];

  return <fieldset className="redaction-editor">
    <legend>주소·전화번호 가림</legend>
    <p>사진 위를 드래그하거나 ‘가림 영역 추가’를 누른 뒤 좌표를 조정해요. 검은 영역을 새 이미지로 다시 그려 원본 byte는 전송하지 않아요.</p>
    <div className="redaction-frame" ref={frame} onPointerDown={begin} onPointerUp={finish}>
      {source && <img src={source} alt="가림 전 견적 미리보기"/>}
      {redactions.map((rect,index)=><button aria-label={`가림 영역 ${index+1} 선택`} type="button" className={`redaction-box ${selected===index?'selected':''}`} key={index} onClick={(event)=>{event.stopPropagation();setSelected(index)}} style={{left:`${rect.x}%`,top:`${rect.y}%`,width:`${rect.width}%`,height:`${rect.height}%`}}/>)}
    </div>
    <div className="redaction-actions"><button className="ghost" type="button" onClick={add}>가림 영역 추가</button><button className="ghost" type="button" disabled={selected===null} onClick={()=>{if(selected===null)return;const next=redactions.filter((_,index)=>index!==selected);onChange(next);setSelected(next.length?Math.min(selected,next.length-1):null)}}>선택 영역 삭제</button></div>
    {active && <div className="rect-fields">{(['x','y','width','height'] as const).map((key)=><label key={key}>{({x:'왼쪽',y:'위쪽',width:'너비',height:'높이'} as const)[key]} (%)<input type="number" min="0" max="100" value={Math.round(active[key])} onChange={(event)=>update(key,Number(event.target.value))}/></label>)}</div>}
    <p className="inline-status" role="status">현재 가림 영역 {redactions.length}개</p>
  </fieldset>;
}
